import api from "@/plugins/api";
import { isStubMode } from "./stub-mode";
import { appendQuery } from "@/shared/utils/url";
import { logger } from "@/infrastructure/services/LoggerService";
import { DocumentStatus, DocumentType, toEditorDocumentType } from "../../domain/types/document";
const BASE_PATH = "documents";
/**
 * Fallback list served in dev builds with no BFF configured, so the screens stay
 * navigable without a backend — see `stub-mode.ts` for the gate (dev-build only;
 * a production config miss must fail loudly, never serve these).
 */
const MOCK_DOCUMENTS = [
    {
        id: "doc-001",
        name: "Annual report 2025",
        type: DocumentType.Pdf,
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    },
    {
        id: "doc-002",
        name: "Donor onboarding letter",
        type: DocumentType.Word,
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    },
    {
        id: "doc-003",
        name: "Campaign budget worksheet",
        type: DocumentType.Excel,
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    },
    {
        id: "doc-004",
        name: "Board meeting deck",
        type: DocumentType.PowerPoint,
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    },
    {
        id: "doc-005",
        name: "Volunteer agreement template",
        type: DocumentType.Pdf,
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
    },
];
function delay(value, ms = 200) {
    return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}
function fileTypeFor(type) {
    switch (type) {
        case DocumentType.Excel:
            return "xlsx";
        case DocumentType.PowerPoint:
            return "pptx";
        case DocumentType.Pdf:
            return "pdf";
        case DocumentType.Word:
        case DocumentType.Other:
        default:
            return "docx";
    }
}
/**
 * Maps a browser MIME type (or extension fallback) to our internal DocumentType
 * enum. Used by the stub-mode `createDocument` path so MOCK_DOCUMENTS gets a
 * sensible badge/icon classification.
 */
function inferType(mimeOrName) {
    const value = mimeOrName.toLowerCase();
    if (value.includes("pdf"))
        return DocumentType.Pdf;
    if (value.includes("spreadsheet") ||
        value.includes("excel") ||
        value.endsWith(".xlsx") ||
        value.endsWith(".ods"))
        return DocumentType.Excel;
    if (value.includes("presentation") ||
        value.includes("powerpoint") ||
        value.endsWith(".pptx") ||
        value.endsWith(".odp"))
        return DocumentType.PowerPoint;
    if (value.includes("word") ||
        value.includes("msword") ||
        value.endsWith(".docx") ||
        value.endsWith(".odt"))
        return DocumentType.Word;
    return DocumentType.Other;
}
function inferTypeFromFile(file) {
    return file.type ? inferType(file.type) : inferType(file.name);
}
/**
 * Extension → MIME map, kept in sync with OPDOC's upload whitelist
 * (`Storage:UploadLimits:AllowedContentTypes`). The backend matches the
 * content type exactly, so these strings MUST be identical to that list.
 */
const UPLOAD_CONTENT_TYPES = {
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    odt: "application/vnd.oasis.opendocument.text",
    ods: "application/vnd.oasis.opendocument.spreadsheet",
    odp: "application/vnd.oasis.opendocument.presentation",
};
/** Error thrown when the selected file's extension is not in the upload whitelist. */
export class UnsupportedFileTypeError extends Error {
    constructor(extension) {
        super(`Unsupported file type: .${extension}`);
        Object.defineProperty(this, "extension", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: extension
        });
        this.name = "UnsupportedFileTypeError";
    }
}
/**
 * Resolves the upload Content-Type from the file **extension** rather than the
 * browser's `file.type`. `file.type` is unreliable (empty / `application/octet-stream`
 * for files the OS has no MIME association for, e.g. docx produced by non-Office
 * tools), which makes OPDOC's exact-match content-type validation reject them.
 * Extension → MIME is deterministic and matches the backend whitelist.
 */
function resolveUploadContentType(file) {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    const mime = UPLOAD_CONTENT_TYPES[ext];
    if (!mime) {
        throw new UnsupportedFileTypeError(ext);
    }
    return mime;
}
/**
 * A request made with `responseType: "blob"` receives its ERROR body as a Blob
 * too, so `getApiErrorCode` sees nothing. Re-hydrate the ProblemDetails JSON
 * into `err.response.data` before rethrowing so the standard error-code
 * pipeline (resolveApiErrorKey) keeps working for blob endpoints.
 */
async function rethrowWithParsedBlobError(err) {
    if (err !== null && typeof err === "object" && "response" in err) {
        const response = err.response;
        if (response && response.data instanceof Blob) {
            try {
                response.data = JSON.parse(await response.data.text());
            }
            catch (parseErr) {
                // Non-JSON error body (proxy HTML page, truncated stream): keep the
                // blob — callers fall back to their generic message.
                logger.debug("[documents-api] blob error body was not JSON", parseErr);
            }
        }
    }
    throw err;
}
/**
 * API client for document operations.
 * All live calls go through the BFF (unwrapEnvelope: true); stubbed today.
 */
export const documentsApi = {
    async getDocuments(params = {}) {
        if (isStubMode()) {
            const term = params.search?.trim().toLowerCase();
            const filtered = MOCK_DOCUMENTS.filter((doc) => (!params.type || doc.type === params.type) &&
                (!term ||
                    doc.name.toLowerCase().includes(term) ||
                    (doc.description?.toLowerCase().includes(term) ?? false)) &&
                (!params.statuses?.length || params.statuses.includes(doc.status ?? "Draft")) &&
                (!params.templateKind || doc.templateKind === params.templateKind));
            if (params.sortBy === "name") {
                filtered.sort((a, b) => params.sortDirection === "asc"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name));
            }
            const page = params.page ?? 1;
            const pageSize = params.pageSize ?? 20;
            const start = (page - 1) * pageSize;
            return delay({
                items: filtered.slice(start, start + pageSize),
                page,
                pageSize,
                totalCount: filtered.length,
                totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
            });
        }
        // Wire names must match the BFF's [FromQuery] parameters exactly (R61):
        // the FE search field is `searchTerm` upstream, statuses repeat as `status`
        // and classification pairs repeat as `classification`.
        const url = appendQuery(BASE_PATH, {
            page: params.page,
            pageSize: params.pageSize,
            searchTerm: params.search?.trim() || undefined,
            type: params.type,
            classification: params.classifications,
            status: params.statuses,
            templateKind: params.templateKind,
            sortBy: params.sortBy,
            sortDirection: params.sortDirection,
            updatedFrom: params.updatedFrom,
            updatedTo: params.updatedTo,
        });
        const response = await api.get(url, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    async getDocument(id) {
        if (isStubMode()) {
            const item = MOCK_DOCUMENTS.find((d) => d.id === id);
            if (!item) {
                throw new Error(`Document ${id} not found`);
            }
            return delay({ ...item, fileType: fileTypeFor(item.type) });
        }
        const response = await api.get(`${BASE_PATH}/${id}`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Returns the config object the FE hands straight to `new DocsAPI.DocEditor(...)`.
     * In stub mode the returned `url`, `callbackUrl`, and `token` are placeholders
     * that the DocumentServer would reject — they exist only so the FE can render
     * the editor page shell pre-backend.
     */
    async getEditorConfig(id) {
        if (isStubMode()) {
            const doc = await this.getDocument(id);
            const fileType = fileTypeFor(doc.type);
            return delay({
                document: {
                    fileType,
                    key: `${doc.id}-${Date.now()}`,
                    title: doc.name,
                    url: doc.fileUrl ?? `https://stub.local/documents/${doc.id}.${fileType}`,
                    permissions: {
                        edit: true,
                        download: true,
                        print: true,
                    },
                },
                documentType: toEditorDocumentType(doc.type),
                editorConfig: {
                    mode: "edit",
                    lang: "en",
                    user: { id: "stub-user", name: "Local Developer" },
                    callbackUrl: `https://stub.local/api/documents/${doc.id}/callback`,
                },
                token: "stub.jwt.token",
            });
        }
        const response = await api.get(`${BASE_PATH}/${id}/editor-config`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Uploads a new document. Body is multipart/form-data:
     *   - `file` (binary, required)
     *   - `Name` (optional display name, defaults to the filename without
     *     extension on the server when omitted)
     *   - `Description` (optional free-text)
     *
     * The BFF binds `Name` / `Description` via `[FromForm(Name = "...")]` so the
     * multipart key casing matters — keep PascalCase here.
     */
    async createDocument(file, name, description, templateKind) {
        if (isStubMode()) {
            const inferred = inferTypeFromFile(file);
            const id = `doc-stub-${Date.now()}`;
            const stub = {
                id,
                name: name?.trim() || file.name.replace(/\.[^/.]+$/, ""),
                type: inferred,
                description: description?.trim() || undefined,
                sizeBytes: file.size,
                updatedAt: new Date().toISOString(),
                templateKind: templateKind ?? "Standardized",
                fileType: fileTypeFor(inferred),
            };
            // Insert at the top of the mock list so the new doc shows on next fetch.
            MOCK_DOCUMENTS.unshift({
                id: stub.id,
                name: stub.name,
                type: stub.type,
                description: stub.description,
                sizeBytes: stub.sizeBytes,
                updatedAt: stub.updatedAt,
                templateKind: stub.templateKind,
            });
            return delay(stub);
        }
        // Resolve the content type from the extension (not the unreliable browser
        // file.type) and re-wrap the file so the multipart part carries a MIME the
        // OPDOC whitelist accepts. Throws UnsupportedFileTypeError for unknown
        // extensions — caught by the upload component to show a localized message.
        const contentType = resolveUploadContentType(file);
        const typedFile = new File([file], file.name, { type: contentType });
        const form = new FormData();
        form.append("file", typedFile);
        if (name && name.trim())
            form.append("Name", name.trim());
        if (description && description.trim())
            form.append("Description", description.trim());
        if (templateKind)
            form.append("TemplateKind", templateKind);
        // Let the browser set the multipart boundary on Content-Type; explicitly
        // override the axios default JSON header so it does not override it.
        const response = await api.post(BASE_PATH, form, {
            unwrapEnvelope: true,
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    },
    /**
     * Renames a document and/or updates its description. PATCH semantics:
     * fields left undefined are not touched. The BFF binds `UpdateDocumentRequest`
     * via `[FromBody]` (System.Text.Json camelCase) — lowercase keys are the wire
     * contract.
     */
    async updateDocument(id, body) {
        if (isStubMode()) {
            const idx = MOCK_DOCUMENTS.findIndex((d) => d.id === id);
            if (idx < 0)
                throw new Error(`Document ${id} not found`);
            if (body.name !== undefined)
                MOCK_DOCUMENTS[idx].name = body.name;
            if (body.description !== undefined)
                MOCK_DOCUMENTS[idx].description = body.description || undefined;
            if (body.contextKey !== undefined)
                MOCK_DOCUMENTS[idx].contextKey = body.contextKey || undefined;
            if (body.documentTypeKey !== undefined)
                MOCK_DOCUMENTS[idx].documentTypeKey = body.documentTypeKey || undefined;
            const updated = MOCK_DOCUMENTS[idx];
            return delay({ ...updated, fileType: fileTypeFor(updated.type) });
        }
        const payload = {};
        if (body.name !== undefined)
            payload.name = body.name;
        if (body.description !== undefined)
            payload.description = body.description;
        if (body.contextKey !== undefined)
            payload.contextKey = body.contextKey;
        if (body.documentTypeKey !== undefined)
            payload.documentTypeKey = body.documentTypeKey;
        const response = await api.patch(`${BASE_PATH}/${id}`, payload, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Duplicates a document (content + metadata) as a new Draft. The optional
     * name overrides the copy's display name; OPDOC resolves name collisions
     * server-side with a language-neutral counter suffix.
     */
    async duplicateDocument(id, name) {
        if (isStubMode()) {
            const source = MOCK_DOCUMENTS.find((d) => d.id === id);
            if (!source)
                throw new Error(`Document ${id} not found`);
            const copy = {
                ...source,
                id: `doc-stub-${Date.now()}`,
                name: name?.trim() || `${source.name} (2)`,
                status: DocumentStatus.Draft,
                updatedAt: new Date().toISOString(),
            };
            MOCK_DOCUMENTS.unshift(copy);
            return delay({ ...copy, fileType: fileTypeFor(copy.type) });
        }
        const body = name && name.trim() ? { name: name.trim() } : {};
        const response = await api.post(`${BASE_PATH}/${id}/duplicate`, body, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Archives a document (Draft or Active → Archived). Archived documents are
     * read-only and excluded from the default list; restore via
     * `activateDocument`.
     */
    async archiveDocument(id) {
        if (isStubMode()) {
            const idx = MOCK_DOCUMENTS.findIndex((d) => d.id === id);
            if (idx < 0)
                throw new Error(`Document ${id} not found`);
            MOCK_DOCUMENTS[idx].status = DocumentStatus.Archived;
            const doc = MOCK_DOCUMENTS[idx];
            return delay({ ...doc, fileType: fileTypeFor(doc.type) });
        }
        const response = await api.post(`${BASE_PATH}/${id}/archive`, undefined, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Hard-deletes a document. The BFF returns 204 No Content; the axios
     * response wrapper unwraps the `ApiResponse` envelope so callers see void.
     * NOTE: not exposed in the library UI (PO decision — archive is the only
     * end-user removal path); kept for programmatic/admin use.
     */
    async deleteDocument(id) {
        if (isStubMode()) {
            const idx = MOCK_DOCUMENTS.findIndex((d) => d.id === id);
            if (idx >= 0)
                MOCK_DOCUMENTS.splice(idx, 1);
            await delay(undefined);
            return;
        }
        await api.delete(`${BASE_PATH}/${id}`, { unwrapEnvelope: true });
    },
    /**
     * Activates a document (publishes a Draft / restores an Archived one).
     * OPDOC refuses invalid merge tags with 422
     * (`Document.Activation.InvalidTags` + `errorResult.invalidTags`).
     */
    async activateDocument(id) {
        if (isStubMode()) {
            const idx = MOCK_DOCUMENTS.findIndex((d) => d.id === id);
            if (idx < 0)
                throw new Error(`Document ${id} not found`);
            MOCK_DOCUMENTS[idx].status = DocumentStatus.Active;
            const doc = MOCK_DOCUMENTS[idx];
            return delay({ ...doc, fileType: fileTypeFor(doc.type) });
        }
        const response = await api.post(`${BASE_PATH}/${id}/activate`, undefined, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Validates the template's merge tags against the registry schema of its
     * classification. Read-only; works on Draft and Active templates.
     */
    async getTagValidation(id) {
        if (isStubMode()) {
            return delay({
                status: "Valid",
                tagCount: 0,
                invalidTags: [],
            });
        }
        const response = await api.get(`${BASE_PATH}/${id}/tag-validation`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Renders the template with a registry-synthesized sample payload
     * ("preview with sample data") and returns the resolved PDF as a Blob.
     * The caller owns the object-URL lifecycle. Error bodies arrive as blobs
     * and are re-hydrated so `getApiErrorCode` can read the `errorCode`.
     */
    async previewWithSampleData(id) {
        if (isStubMode()) {
            // Minimal one-page PDF so the preview modal renders something locally.
            return delay(new Blob(["%PDF-1.4\n%stub preview\n%%EOF"], { type: "application/pdf" }));
        }
        try {
            const response = await api.post(`${BASE_PATH}/${id}/preview-sample`, undefined, {
                unwrapEnvelope: true,
                responseType: "blob",
            });
            return response.data;
        }
        catch (err) {
            return rethrowWithParsedBlobError(err);
        }
    },
};
